import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';

const bgDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark Services',
    },
  },
};

export function StrategyHero() {
  const bg = useSlotGroup('strategyhero.background', bgDef);

  return (
    <section className="relative flex h-[min(70svh,684px)] w-full flex-col overflow-clip bg-[rgb(var(--ala-dark))] md:h-[684px]">
      <EditableSlotGroup groupPath="strategyhero.background" definition={bgDef}>
        <div className="absolute inset-0 z-0">
          <VibesitesImage
            url={
              bg.image ||
              ''
            }
            alt={bg.alt || ''}
            priority
            sizes="100vw"
            className="h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(91.65deg, rgb(var(--ala-primary)/1) 0%, rgba(0,0,0,0) 100%)',
            }}
          />
        </div>
      </EditableSlotGroup>

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1440px] flex-col justify-end px-6 pb-12 md:px-[clamp(16px,6vw,87px)] md:pb-[25px]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="mb-4 flex items-center gap-3">
            <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-secondary))]" aria-hidden />
            <RichTextContent
              path="strategyhero.eyebrow"
              label="Eyebrow"
              as="span"
              className="font-heading text-base font-medium uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-on-primary))]"
            />
          </div>
          <RichTextContent
            path="strategyhero.title"
            label="Title"
            as="h1"
            className="font-heading text-7xl font-semibold leading-[1.35] text-[rgb(var(--ala-on-primary))]"
          />
        </motion.div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'StrategyHero',
  displayName: 'Strategy Hero',
  tokens: {
    'strategyhero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>American Landmark Strategy</p>',
    },
    'strategyhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Our Services</p>',
    },
  },
  slotGroups: {
    'strategyhero.background': bgDef,
  },
};

(StrategyHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default StrategyHero;
